import { Column } from "ka-table/models";
import CrmTable from "../../App/Components/Table/CrmTable";
import {
  useDeleteMessageCollection,
  useGetScheduleMessageCollections,
} from "../../App/Queries/MessageQueries";
import { DataType } from "ka-table";
import { useState } from "react";
import { IconButton, Stack, Typography } from "@mui/material";
import { RemoveCircle } from "@mui/icons-material";
import { getNowUtc } from "../../MessagingCenter/Utils";
import { ConfirmationDialog } from "../../App/Components/ConfirmationDialog/ConfirmationDialog";
import { useNotification } from "../../App/State/Hooks/notificationHooks";

const columns: Column[] = [
  { key: "createdDate", title: "Created", dataType: DataType.Date, isSortable: false },
  { key: "title", title: "Title", dataType: DataType.String, isSortable: false },
  { key: "messageCount", title: "Messages (count)", dataType: DataType.Number, isSortable: false },
  { key: "earliestScheduledDate", title: "Earliest Send", dataType: DataType.Date, isSortable: false },
  { key: "latestScheduledDate", title: "Last Send", dataType: DataType.Date, isSortable: false },
  {
    key: "actions",
    title: "Actions",
    dataType: DataType.Object,
    isEditable: false,
    isSortable: false,
    isResizable: false,
    isFilterable: false,
    width: 100,
  },
];

export default function ScheduleMessagesCollectionsTable({ groupId }: { groupId: string }) {
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [deletedIds, setDeletedIds] = useState<string[]>([]);

  const { displaySuccess, displayError } = useNotification();
  const { data: messageCollections, isLoading } = useGetScheduleMessageCollections(groupId, page, pageSize);

  const { mutate: deleteCollection } = useDeleteMessageCollection();

  const handleDeleteClick = (collectionId: string) => {
    setSelectedId(collectionId);
    setDeleteOpen(true);
  };

  const deleteMessageCollection = () => {
    setDeleteOpen(false);
    if (selectedId) {
      setDeletedIds([...deletedIds, selectedId]);
      deleteCollection(selectedId, {
        onSettled: (isSuccess, error) => {
          if (error || !isSuccess) {
            displayError("Error deleting message collection.");
          } else {
            displaySuccess(
              "Message collection deleted successfully. Deleting could take a moment to complete.",
            );
          }
        },
      });
    }
  };

  const tableData =
    messageCollections?.items?.map((r) => {
      return {
        ...r,
        actions: (
          <Stack direction="row" display="flex" justifyContent="center">
            <IconButton
              data-test="cancel-delete-scheduled-message"
              color="error"
              title="Cancel/Delete this scheduled message."
              disabled={
                !r.latestScheduledDate || r.latestScheduledDate < getNowUtc() || deletedIds.includes(r.id)
              }
              onClick={(event) => {
                event.stopPropagation();
                handleDeleteClick(r.id);
              }}
              sx={{
                border: "2px solid rgba(0,0,0,0.3)",
                transition: "all 0.2s ease-in-out",
                "&:hover": { backgroundColor: "error.main", color: "white" },
              }}
            >
              <RemoveCircle sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Stack>
        ),
      };
    }) ?? [];

  return (
    <>
      <CrmTable
        columns={columns}
        data={tableData}
        loading={isLoading}
        name={`scheduled-message-collections-${groupId}`}
        pagination={{
          enabled: true,
          rowsPerPage: [10, 25, 50, 100],
          currentPage: page - 1,
          handleChangePage: (_ev, newPage) => setPage(newPage),
          handleChangeRowsPerPage: (ev) => setPageSize(ev.target.value as unknown as number),
          rows: pageSize,
          totalCount: messageCollections?.totalItems ?? 0,
        }}
        ExportButton={undefined}
        disableSelectAll={true}
      />
      <ConfirmationDialog
        cancelMessage="Cancel"
        confirmMessage="Delete"
        isOpen={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        title="Delete Message Collection"
        handleCancel={() => setDeleteOpen(false)}
        handleConfirm={() => deleteMessageCollection()}
        message={
          <>
            <Typography>Are you sure you want to delete this collection?</Typography>
            <Typography>
              Messages in this collection that have not yet been sent will not be delivered.
            </Typography>
          </>
        }
      />
    </>
  );
}
